import { useRef, useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import {
  useActiveAccount,
  useActiveWallet,
  useActiveWalletChain,
  useSwitchActiveWalletChain,
} from "thirdweb/react";
import {
  createThirdwebClient,
  getContract,
  prepareContractCall,
  sendTransaction,
} from "thirdweb";
import { ThirdwebWalletConnect } from "../components/ThirdwebWalletConnect";
import { useHttp } from "../hooks/useHttp";
import { logger } from "../utils/logger";
import { ACTIVE_CHAIN, ACTIVE_CHAIN_ID } from "../config/chain";
import { inMetamaskBrowser, openInMetamask } from "../utils/metamask";
import { useWalletKind } from "../hooks/useWalletKind";

const carouselCards = [
  {
    id: 1,
    image: "/slider/how-to-claim/1.png",
    title: "Tap the chip",
    description:
      "Hold your phone near the Chimpers tag. A page will open, click <span class='font-medium text-black'>Claim now</span> or <span class='font-medium text-black'>Sign Up</span>.",
  },
  {
    id: 2,
    image: "/slider/how-to-claim/2.png",
    title: "Connect & confirm",
    description:
      "Choose your wallet app and connect it. When asked, <span class='font-medium text-black'>approve the connection</span>. You'll see 'Connected successfully'. Close the tab and <span class='font-medium text-black'>tap the tag again</span>.",
  },
  {
    id: 3,
    image: "/slider/how-to-claim/3.png",
    title: "Claim your digital collectible",
    description:
      "The collectible preview will appear. Tap Claim and confirm once more in your wallet. You'll see a success message. Your Chimpers collectible is now yours!",
  },
];

// Drawer types for the flow
type DrawerType =
  | "loading"
  | "wallet_connect"
  | "open_in_metamask"
  | "scan_chip"
  | "sign_message"
  | "scan_again"
  | "claim_nft"
  | "reclaim_confirm"
  | "claiming"
  | "success"
  | "already_claimed"
  | "error"
  | null;

// API Response interface
interface NfcClaimResponse {
  success: boolean;
  action:
    | "bind_wallet"
    | "claim"
    | "reclaim"
    | "already_claimed"
    | "already_owner"
    | "reclaim_cooldown"
    | "reclaim_disabled"
    | "session_invalid"
    | "session_expired"
    | "counter_exceeded";
  uid?: string;
  nfcSid?: string; // Session ID returned on first scan (bind_wallet action)
  toyType?: number;
  ctr?: number;
  timestamp?: number;
  signature?: string;
  message: string;
  // Reclaim-only fields
  isReclaim?: boolean; // present on bind_wallet response when the flow is a reclaim
  currentOwner?: string; // present on bind_wallet (reclaim) — on-chain owner pre-reclaim
  previousOwner?: string; // present on reclaim action — owner that's about to lose the NFT
  tokenId?: number; // present on reclaim action
  waitSeconds?: number; // present on reclaim_cooldown
  readyAtSeconds?: number; // present on reclaim_cooldown
}
export const ClaimChimpers = () => {
  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sendRequest } = useHttp();

  // Wallet hooks
  const account = useActiveAccount();
  const wallet = useActiveWallet();

  const address = account?.address;
  const eoaAccount = useMemo(() => {
    return (
      (wallet as any)?.getAdminAccount?.() ||
      (wallet as any)?.getAccount?.() ||
      account
    );
  }, [wallet, account]);
  const eoaAddress = eoaAccount?.address;
  const activeWalletChain = useActiveWalletChain();
  const switchActiveWalletChain = useSwitchActiveWalletChain();
  console.log("activeWalletChain", activeWalletChain);
  const walletKind = useWalletKind();
  // URL parameters
  const uid = searchParams.get("uid");
  const ctr = searchParams.get("ctr");
  const cmac = searchParams.get("cmac");

  // Check if user came with NFC scan parameters
  const hasNfcParams = !!(uid && ctr && cmac);

  // State management
  const [currentDrawer, setCurrentDrawer] = useState<DrawerType>(null);
  const [nfcClaimResponse, setNfcClaimResponse] =
    useState<NfcClaimResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isSwitchingChain, setIsSwitchingChain] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);

  // Contract configuration
  const contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS;
  const requiredChain = ACTIVE_CHAIN;
  const settings = {
    dots: false,
    infinite: false,
    speed: 450,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    arrows: false,
    afterChange: (current: number) => {
      setCurrentSlide(current);
    },
  };

  // Validate NFC parameters, then wait for wallet + environment gating before calling backend
  useEffect(() => {
    let isMounted = true;

    const validateParams = (): string | null => {
      if (!uid || !ctr || !cmac) {
        logger.ui("Claim page loaded without NFC parameters");
        return "missing";
      }
      if (!/^[A-F0-9]+$/i.test(uid)) return "Invalid UID format";
      if (!/^[A-F0-9]+$/i.test(ctr)) return "Invalid counter format";
      if (!/^[A-F0-9]+$/i.test(cmac)) return "Invalid CMAC format";
      return null;
    };

    const proceedAfterGating = async () => {
      if (!isMounted) return;
      setCurrentDrawer("loading");
      setError(null);
      try {
        logger.api("Calling /nfc_claim API with validated parameters", {
          uid,
          ctr,
          cmac,
        });
        const { data } = await sendRequest<NfcClaimResponse>({
          endpoint: `/api/nfc_claim?uid=${encodeURIComponent(
            uid as string
          )}&ctr=${encodeURIComponent(ctr as string)}&cmac=${encodeURIComponent(
            cmac as string
          )}`,
          method: "GET",
          responseType: "json",
        });
        if (!isMounted) return;
        logger.api("NFC claim API response received", { response: data });
        setNfcClaimResponse(data);
        if (!data.success) {
          setError(data.message);
          setCurrentDrawer("error");
          return;
        }
        switch (data.action) {
          case "bind_wallet":
            setCurrentDrawer("wallet_connect");
            break;
          case "claim":
            setCurrentDrawer("claim_nft");
            break;
          case "reclaim":
            // Show explicit confirmation before transferring the NFT away
            // from its current owner.
            setCurrentDrawer("reclaim_confirm");
            break;
          case "already_claimed":
            setError(
              data.message ||
                "This Digital Collectible has already been claimed"
            );
            setCurrentDrawer("already_claimed");
            break;
          case "already_owner":
            setError(
              data.message ||
                "This Digital Collectible is already in your wallet"
            );
            setCurrentDrawer("already_claimed");
            break;
          case "reclaim_disabled":
            setError(
              data.message ||
                "Reclaiming is currently disabled for this collectible"
            );
            setCurrentDrawer("already_claimed");
            break;
          case "reclaim_cooldown":
            setError(
              data.message ||
                "Reclaim cooldown active — please try again later"
            );
            setCurrentDrawer("error");
            break;
          case "session_invalid":
            setError(
              data.message || "Invalid session. Please scan the chip again."
            );
            setCurrentDrawer("error");
            break;
          case "session_expired":
            setError(
              data.message ||
                "Session expired (5 min timeout). Please scan the chip again."
            );
            setCurrentDrawer("error");
            break;
          case "counter_exceeded":
            setError(
              data.message || "Too many scans. Please scan the chip again."
            );
            setCurrentDrawer("error");
            break;
          default:
            setError("Unknown response from server");
            setCurrentDrawer("error");
        }
      } catch (err: any) {
        if (!isMounted) return;
        if (err?.name === "AbortError" || err?.message?.includes("abort")) {
          logger.ui(
            "Request aborted (component unmounted or new request started)"
          );
          return;
        }
        logger.apiError("NFC claim API call error", err as Error);
        setError(
          err instanceof Error ? err.message : "Failed to validate NFC scan"
        );
        setCurrentDrawer("error");
      }
    };

    const run = async () => {
      const paramErr = validateParams();
      if (paramErr) {
        if (paramErr === "missing") return; // no NFC flow
        setError(paramErr);
        setCurrentDrawer("error");
        return;
      }

      console.log("walletKind", walletKind);
      console.log("inMetamaskBrowser()", inMetamaskBrowser());
      // Require wallet selection first to determine environment
      if (!address) {
        setCurrentDrawer("wallet_connect");
        return;
      }
      // If user chose MetaMask but is not in MetaMask browser, prompt to open in MetaMask app
      if (walletKind === "metamask" && !inMetamaskBrowser()) {
        setCurrentDrawer("open_in_metamask");
        return;
      }

      // Safe to proceed with DB/cookie update now
      await proceedAfterGating();
    };

    run();
    return () => {
      isMounted = false;
    };
  }, [uid, ctr, cmac, address, wallet, sendRequest]);

  // When wallet connects without NFC params, show scan chip drawer
  useEffect(() => {
    if (address && currentDrawer === "wallet_connect" && !hasNfcParams) {
      setCurrentDrawer("scan_chip");
    }
  }, [address, currentDrawer, hasNfcParams]);

  // After NFC params validated and wallet connected, move to sign message
  useEffect(() => {
    if (
      hasNfcParams &&
      address &&
      nfcClaimResponse &&
      currentDrawer === "wallet_connect"
    ) {
      setCurrentDrawer("sign_message");
    }
  }, [hasNfcParams, address, nfcClaimResponse, currentDrawer]);

  // Safety gate: if we reach sign step with MetaMask but not in MetaMask browser, prompt to open MetaMask
  useEffect(() => {
    if (
      currentDrawer === "sign_message" &&
      walletKind === "metamask" &&
      !inMetamaskBrowser()
    ) {
      setCurrentDrawer("open_in_metamask");
    }
  }, [currentDrawer, walletKind]);

  useEffect(() => {
    if (currentDrawer) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [currentDrawer]);

  // Handle wallet signing and binding
  const handleSignMessage = async () => {
    if (!address || !account || !uid || !nfcClaimResponse) return;

    // Get nfcSid from the first scan response
    const nfcSid = nfcClaimResponse.nfcSid;

    if (!nfcSid) {
      setError("Session ID not found. Please scan the chip again.");
      setCurrentDrawer("error");
      return;
    }

    setIsSigning(true);
    setError(null);

    try {
      // Create user-friendly message according to API guide (lines 136-139)
      const message = `I am connecting my wallet to claim the Digital Collectible for chip: ${uid}\n\nSession ID: ${nfcSid}`;

      logger.wallet("Signing message for wallet binding", {
        uid,
        nfcSid: nfcSid.substring(0, 10) + "...", // Log partial nfcSid for privacy
        messagePreview: `I am connecting my wallet to claim the Digital Collectible for chip: ${uid}...`,
      });

      // Use EOA for signature to match backend ethers.verifyMessage
      const eoaAccount =
        (wallet as any)?.getAdminAccount?.() ||
        (wallet as any)?.getAccount?.() ||
        account;
      const eoaAddress = eoaAccount?.address;
      console.log("eoaAddress", eoaAddress);
      const signature = await eoaAccount.signMessage({ message });
      console.log("signature", signature);
      console.log("message", message);
      console.log("address", address);
      console.log("account", account);
      console.log("uid", uid);
      console.log("nfcSid", nfcSid);
      console.log("requiredChain", requiredChain);
      console.log("activeWalletChain", activeWalletChain);
      console.log("ACTIVE_CHAIN", ACTIVE_CHAIN);
      // Bind wallet to UID
      logger.api("Binding wallet", { uid, address: eoaAddress });
      const { data } = await sendRequest<{ success: boolean; message: string }>(
        {
          endpoint: `/api/nfc_bind_wallet?uid=${encodeURIComponent(
            uid
          )}&address=${encodeURIComponent(
            eoaAddress
          )}&signature=${encodeURIComponent(signature)}`,
          method: "GET",
        }
      );

      logger.wallet("Wallet bound successfully", { response: data });
      setCurrentDrawer("scan_again");
    } catch (err: any) {
      logger.walletError("Failed to sign and bind wallet", err as Error);

      // Check if user rejected the signature
      if (
        err?.code === 4001 ||
        err?.code === "ACTION_REJECTED" ||
        err?.message?.toLowerCase().includes("user rejected") ||
        err?.message?.toLowerCase().includes("user denied") ||
        err?.message?.toLowerCase().includes("user cancelled")
      ) {
        // User cancelled - just show error in the current drawer, don't change drawer
        setError("Signature cancelled. Please try again.");
      } else if (
        err?.message?.includes("No session") ||
        err?.message?.includes("Invalid signature") ||
        err?.message?.includes("Missing params")
      ) {
        // API errors from nfc_bind_wallet - show error drawer
        setError(
          err instanceof Error
            ? err.message
            : "Session error. Please scan the chip again."
        );
        setCurrentDrawer("error");
      } else {
        // Other errors - show in current drawer
        setError(err instanceof Error ? err.message : "Failed to sign message");
      }
    } finally {
      setIsSigning(false);
    }
  };

  // Handle network switching
  const handleSwitchNetwork = async () => {
    if (!switchActiveWalletChain) return;
    console.log("switchActiveWalletChain", switchActiveWalletChain);
    setIsSwitchingChain(true);
    setError(null);

    try {
      logger.wallet("Switching network", {
        chainId: ACTIVE_CHAIN_ID,
      });
      await switchActiveWalletChain(requiredChain);
      logger.wallet("Network switched successfully");
      // Clear error after successful switch
      setError(null);
    } catch (err: any) {
      logger.walletError("Failed to switch network", err as Error);

      // Check if user rejected the network switch
      if (
        err?.code === 4001 ||
        err?.code === "ACTION_REJECTED" ||
        err?.message?.toLowerCase().includes("user rejected") ||
        err?.message?.toLowerCase().includes("user denied") ||
        err?.message?.toLowerCase().includes("user cancelled")
      ) {
        setError(
          "Network switch cancelled. Please switch to Avalanche Fuji to continue."
        );
      } else {
        setError(
          "Failed to switch network. Please switch manually in your wallet."
        );
      }
    } finally {
      setIsSwitchingChain(false);
    }
  };

  // Handle NFT claim via smart contract.
  // Same contract method is used for both fresh claim and reclaim — the
  // contract decides which branch to take based on UID state.
  const handleClaimNFT = async () => {
    if (
      !address ||
      !account ||
      !nfcClaimResponse ||
      (nfcClaimResponse.action !== "claim" &&
        nfcClaimResponse.action !== "reclaim")
    ) {
      return;
    }
    const isReclaim = nfcClaimResponse.action === "reclaim";

    const {
      uid: claimUid,
      toyType,
      ctr: claimCtr,
      timestamp,
      signature,
    } = nfcClaimResponse;

    if (
      !claimUid ||
      toyType === undefined ||
      claimCtr === undefined ||
      !timestamp ||
      !signature
    ) {
      setError("Missing claim parameters");
      return;
    }

    // Check if on correct network
    if ((activeWalletChain?.id || 0) !== ACTIVE_CHAIN.id) {
      setError("Please switch to the target network");
      return;
    }

    setIsClaiming(true);
    setCurrentDrawer("claiming");
    setError(null);

    try {
      // Load ABI
      logger.contract("Loading ABI");
      const response = await fetch("/NFC223Abi.json");
      const NFC223Abi = await response.json();
      logger.contract("ABI loaded successfully");

      if (!contractAddress) {
        throw new Error("Contract address not configured");
      }

      const client = createThirdwebClient({
        clientId: process.env.REACT_APP_THIRDWEB_CLIENT_ID as string,
      });
      const chain = requiredChain;
      console.log("chain requiredChain", chain);
      const contract = getContract({
        client,
        chain,
        address: contractAddress,
        abi: NFC223Abi,
      });

      logger.contract("Calling claim function", {
        toyType,
        ctr: claimCtr,
        timestamp,
        uid: claimUid,
        signatureLength: signature.length,
      });

      const eoaAccount =
        (wallet as any)?.getAdminAccount?.() ||
        (wallet as any)?.getAccount?.() ||
        account;
      const eoaAddress = eoaAccount?.address;
      console.log("eoaAddress", eoaAddress);

      const transaction = prepareContractCall({
        contract,
        method: "claim",
        // New ABI: claim(toyType, ctr, timestamp, caller, uid, signature)
        params: [toyType, claimCtr, timestamp, eoaAddress, claimUid, signature],
      });
      const receipt = await sendTransaction({
        account,
        transaction,
      });
      const txHashResult = (receipt as any)?.transactionHash;
      setTxHash(txHashResult || null);
      logger.contract("Transaction submitted", { txHash: txHashResult });

      logger.contract("Transaction submitted/queued", {
        txHash: txHashResult,
      });

      if (txHashResult) {
        logger.contract("Digital Collectible claimed successfully", {
          txHash: txHashResult,
          uid: claimUid,
          toyType,
        });
        setCurrentDrawer("success");
      } else {
        throw new Error("Transaction failed");
      }
    } catch (err: any) {
      logger.contractError(
        "Failed to claim Digital Collectible",
        err as Error,
        {
          uid: claimUid,
          toyType,
          address,
          contractAddress,
        }
      );

      // Check if user rejected the transaction
      if (
        err?.code === 4001 ||
        err?.code === "ACTION_REJECTED" ||
        err?.message?.toLowerCase().includes("user rejected") ||
        err?.message?.toLowerCase().includes("user denied") ||
        err?.message?.toLowerCase().includes("user cancelled")
      ) {
        // User cancelled transaction - go back to the appropriate drawer
        setError("Transaction cancelled. Please try again.");
        setCurrentDrawer(isReclaim ? "reclaim_confirm" : "claim_nft");
      } else {
        // Other errors - show error drawer
        setError(
          err instanceof Error
            ? err.message
            : "Failed to claim Digital Collectible"
        );
        setCurrentDrawer("error");
      }
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <div className="py-5">
      <div className="px-6 mb-8">
        {/* Show dragon image block only if user came with NFC params */}
        {hasNfcParams && (
          <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mb-4">
            {/* Gradient background placeholder */}
            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 animate-pulse" />

            {/* Dynamic toy image */}
            {nfcClaimResponse?.toyType !== undefined ? (
              <img
                src={`/images/toys/${
                  (nfcClaimResponse.toyType as number) + 1
                }.svg`}
                alt={`Toy Type ${nfcClaimResponse.toyType}`}
                className="object-cover absolute inset-0 w-full h-full"
                onError={(e) => {
                  // Hide image if it fails to load, showing gradient instead
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <img
                src={`/images/chimpers-certificate.png`}
                className="object-cover absolute inset-0 w-full h-full"
              />
            )}

            <img
              src="/images/223-logo.png"
              alt="223-logo"
              className="absolute top-4 left-4 max-w-[83px]"
            />

            <img
              src="/images/chimpers-logo.png"
              alt="chimpers-logo"
              className="absolute top-4 right-4 max-w-[50px]"
            />

            <div className="backdrop-blur-3xl rounded-[19px] py-4 absolute bottom-3 right-3 left-3 bg-white/10">
              <div className="max-w-[240px] mx-auto flex flex-col items-center justify-center gap-2">
                <h3 className="text-lg font-bold text-center">
                  223 x Chimpers Authenticity Certificate
                </h3>
              </div>
            </div>
          </div>
        )}

        {/* Show sign up block only if user didn't come with NFC params */}
        {!hasNfcParams && (
          <div className="py-[22px] rounded-[12px] bg-gradient-to-r from-[#3FA3FF] to-[#FF5A7F] flex flex-col items-center justify-center gap-2">
            <div className="font-bold text-center text-white">
              Sign up for 223 and Claim <br /> Digital Collectibles in a Flash
            </div>

            <button
              onClick={() => setCurrentDrawer("wallet_connect")}
              className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 hover:bg-[#4a4a4a] transition-colors cursor-pointer"
            >
              Claim Now
            </button>
          </div>
        )}
      </div>

      <div className="px-6">
        <h3 className="mb-4 text-2xl font-bold text-center">How its Works</h3>

        <div className="relative carousel-container">
          <Slider ref={sliderRef} {...settings}>
            {carouselCards.map((card) => (
              <CarouselCard key={card.id} card={card} />
            ))}
          </Slider>
        </div>

        <div className="my-3 text-center">
          <h4 className="mb-2 text-xl font-bold">
            {carouselCards[currentSlide].title}
          </h4>

          <p
            className="text-lg leading-tight text-[#626262] whitespace-pre-line"
            dangerouslySetInnerHTML={{
              __html: carouselCards[currentSlide].description,
            }}
          ></p>
        </div>

        <div className="flex gap-2 justify-center items-center mt-5 mb-6">
          {carouselCards.map((_, index) => (
            <div
              key={index}
              className={`rounded-full cursor-pointer transition-all ${
                currentSlide === index
                  ? "w-[37px] h-[10px] bg-[#000000]"
                  : "w-[10px] h-[10px] bg-[#CCCCCC]"
              }`}
              onClick={() => sliderRef.current?.slickGoTo(index)}
            />
          ))}
        </div>
      </div>

      {/* Loading Drawer */}
      {currentDrawer === "loading" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full animate-spin">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>

            <div className="text-center space-y-2.5 mt-8">
              <div className="text-2xl font-bold text-black">
                Validating NFC Scan
              </div>
              <div className="text-[#626262]">
                Please wait while we verify your NFC chip...
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Connect Drawer */}
      {currentDrawer === "wallet_connect" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="mb-4">
              <img src="/svg/223-logo.svg" alt="223 Logo" />
            </div>

            <div className="text-center space-y-2.5">
              <div className="text-2xl font-bold text-black">
                Sign In
              </div>

              <div className="text-[#626262]">
              To claim your 223 Digital Collectible, sign in or create an account
              </div>

              <div className="!mt-6">
                <ThirdwebWalletConnect showDropdown={false} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Open in MetaMask Drawer */}
      {currentDrawer === "open_in_metamask" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="text-center space-y-2.5">
              <div className="text-2xl font-bold text-black">
                Open in MetaMask Browser
              </div>

              <div className="text-[#626262]">
                To continue with MetaMask, please open this page in the MetaMask
                browser.
              </div>

              <button
                onClick={() => openInMetamask()}
                className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 mt-4"
              >
                Open in MetaMask Browser
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scan Chip Drawer */}
      {currentDrawer === "scan_chip" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img src="/svg/scan-chip.svg" alt="scan-chip" />

            <div className="w-full">
              <div className="text-2xl font-bold text-center text-black">
                Please scan the NFC chip
              </div>

              <button
                onClick={() => setCurrentDrawer(null)}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-6"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sign Message Drawer */}
      {currentDrawer === "sign_message" && eoaAddress && nfcClaimResponse && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            <div className="text-center">
              <h2 className="mb-2 text-xl font-bold">Sign Message</h2>

              <p className="mb-6 text-gray-600">
                Please sign this message to verify wallet ownership and complete
                the connection process.
              </p>

              {error && (
                <div className="px-4 py-3 mb-4 text-sm text-red-700 bg-red-100 rounded border border-red-400">
                  {error}
                </div>
              )}

              <div className="px-4 py-3 mb-6 text-green-700 bg-[#DCFCE8] rounded border-2 border-[#17803D]">
                <p className="font-semibold">Wallet Connected</p>
                <p className="mt-1 text-sm">
                  {eoaAddress.slice(0, 7)}...{eoaAddress.slice(-6)}
                </p>
              </div>

              {(activeWalletChain?.id || 0) !== ACTIVE_CHAIN_ID && (
                <div className="mb-6">
                  <div className="px-4 py-3 mb-2 text-sm text-yellow-700 bg-yellow-100 rounded border border-yellow-400">
                    Please switch to the target network
                  </div>
                  <button
                    onClick={handleSwitchNetwork}
                    disabled={isSwitchingChain}
                    className="py-2 w-full text-lg font-bold text-white bg-orange-600 transition-colors duration-200 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSwitchingChain ? "Switching..." : "Switch Network"}
                  </button>
                </div>
              )}

              <button
                onClick={handleSignMessage}
                disabled={
                  isSigning || (activeWalletChain?.id || 0) !== ACTIVE_CHAIN_ID
                }
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSigning ? "Signing..." : "Sign Message to Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scan Again Drawer */}
      {currentDrawer === "scan_again" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-12 max-h-fit flex justify-center flex-col items-center">
            <img src="/svg/wallet-connected.svg" alt="wallet-connected" />

            <div className="text-center space-y-2.5 mt-8">
              <div className="text-2xl font-bold text-black">
                Connected Successfully!
              </div>

              <div className="text-[#626262] leading-relaxed">
                Wallet connected successfully! Please{" "}
                <span className="font-semibold text-black">close this tab</span>{" "}
                (do not refresh) and tap the NFC chip again within 5 minutes to
                complete your{" "}
                {nfcClaimResponse?.isReclaim ? "reclaim" : "claim"}.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claim NFT Drawer */}
      {currentDrawer === "claim_nft" && nfcClaimResponse && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            {/* NFT Image Block */}
            <div className="space-y-2.5">
              <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mx-auto">
                <img
                  src={`/images/toys/${
                    nfcClaimResponse.toyType !== undefined
                      ? nfcClaimResponse.toyType + 1
                      : 1
                  }.png`}
                  alt={`Toy Type ${
                    nfcClaimResponse.toyType !== undefined
                      ? nfcClaimResponse.toyType
                      : 1
                  }`}
                  className="object-cover absolute inset-0 w-full h-full"
                  onError={(e) => {
                    // Fallback to dragon image if toy image not found
                    (e.target as HTMLImageElement).src = "/images/dragon.png";
                  }}
                />

                <img
                  src="/images/223-logo.png"
                  alt="223-logo"
                  className="absolute top-4 left-4 max-w-[60px]"
                />

                <img
                  src="/images/chimpers-logo.png"
                  alt="chimpers-logo"
                  className="absolute top-4 right-4 max-w-[50px]"
                />

                <div className="backdrop-blur-md rounded-[19px] py-4 absolute bottom-3 right-3 left-3 bg-white/10">
                  <div className="max-w-[240px] mx-auto flex flex-col items-center justify-center gap-2">
                    <h3 className="text-lg font-bold text-center">
                      223 x Chimpers Authenticity Certificate
                    </h3>

                    {(activeWalletChain?.id || 0) === ACTIVE_CHAIN_ID && (
                      <button
                        onClick={handleClaimNFT}
                        disabled={isClaiming}
                        className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 disabled:opacity-50"
                      >
                        Claim Now
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className="px-4 py-3 text-sm text-red-700 bg-red-100 rounded border border-red-400">
                  {error}
                </div>
              )}

              {(activeWalletChain?.id || 0) !== ACTIVE_CHAIN_ID && (
                <div>
                  <div className="px-4 py-3 mb-2 text-sm text-yellow-700 bg-yellow-100 rounded border border-yellow-400">
                    Please switch to the target network
                  </div>

                  <button
                    onClick={handleSwitchNetwork}
                    disabled={isSwitchingChain}
                    className="py-2 w-full text-lg font-bold text-white bg-orange-600 transition-colors duration-200 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSwitchingChain ? "Switching..." : "Switch Network"}
                  </button>
                </div>
              )}

              {(activeWalletChain?.id || 0) === ACTIVE_CHAIN_ID &&
                nfcClaimResponse.toyType !== undefined && (
                  <div className="text-center text-[22px] font-bold">
                    Toy Type {nfcClaimResponse.toyType}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Reclaim Confirmation Drawer */}
      {currentDrawer === "reclaim_confirm" && nfcClaimResponse && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            <div className="space-y-2.5">
              <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mx-auto">
                <img
                  src={`/images/toys/${
                    nfcClaimResponse.toyType !== undefined
                      ? nfcClaimResponse.toyType + 1
                      : 1
                  }.png`}
                  alt={`Toy Type ${
                    nfcClaimResponse.toyType !== undefined
                      ? nfcClaimResponse.toyType
                      : 1
                  }`}
                  className="object-cover absolute inset-0 w-full h-full"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/images/dragon.png";
                  }}
                />

                <img
                  src="/images/223-logo.png"
                  alt="223-logo"
                  className="absolute top-4 left-4 max-w-[60px]"
                />

                <img
                  src="/images/chimpers-logo.png"
                  alt="chimpers-logo"
                  className="absolute top-4 right-4 max-w-[50px]"
                />

                <div className="backdrop-blur-md rounded-[19px] py-4 absolute bottom-3 right-3 left-3 bg-white/10">
                  <div className="max-w-[240px] mx-auto flex flex-col items-center justify-center gap-2">
                    <h3 className="text-lg font-bold text-center">
                      223 x Chimpers Authenticity Certificate
                    </h3>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <h2 className="mb-2 text-xl font-bold">Reclaim this Collectible</h2>
                <p className="mb-3 text-gray-600">
                  This Digital Collectible is currently owned by another wallet. Reclaiming will
                  <span className="font-semibold text-black"> transfer it to your wallet</span> —
                  the previous owner will no longer hold it.
                </p>

                {nfcClaimResponse.previousOwner && (
                  <div className="px-4 py-3 mb-4 text-sm text-gray-700 bg-gray-100 rounded border border-gray-300">
                    <div className="font-semibold">Current owner</div>
                    <div className="break-all">
                      {nfcClaimResponse.previousOwner.slice(0, 10)}…
                      {nfcClaimResponse.previousOwner.slice(-8)}
                    </div>
                  </div>
                )}

                {error && (
                  <div className="px-4 py-3 mb-4 text-sm text-red-700 bg-red-100 rounded border border-red-400">
                    {error}
                  </div>
                )}

                {(activeWalletChain?.id || 0) !== ACTIVE_CHAIN_ID && (
                  <div className="mb-4">
                    <div className="px-4 py-3 mb-2 text-sm text-yellow-700 bg-yellow-100 rounded border border-yellow-400">
                      Please switch to the target network
                    </div>
                    <button
                      onClick={handleSwitchNetwork}
                      disabled={isSwitchingChain}
                      className="py-2 w-full text-lg font-bold text-white bg-orange-600 transition-colors duration-200 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSwitchingChain ? "Switching..." : "Switch Network"}
                    </button>
                  </div>
                )}

                <button
                  onClick={handleClaimNFT}
                  disabled={
                    isClaiming || (activeWalletChain?.id || 0) !== ACTIVE_CHAIN_ID
                  }
                  className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isClaiming ? "Reclaiming..." : "Reclaim Now"}
                </button>

                <button
                  onClick={() => setCurrentDrawer(null)}
                  className="mt-3 text-sm text-gray-500 underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claiming (Processing) Drawer */}
      {currentDrawer === "claiming" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img src="/svg/claiming.svg" alt="claiming" />

            <div className="text-center space-y-2.5 mt-8">
              <div className="text-2xl font-bold text-black">
                {nfcClaimResponse?.action === "reclaim"
                  ? "Reclaiming"
                  : "Claiming"}
              </div>

              <div className="text-[#626262]">
                {nfcClaimResponse?.action === "reclaim"
                  ? "Reclaiming your Digital Collectible. Please wait."
                  : "Claiming your Digital Collectible. Please wait."}
              </div>

              {txHash && (
                <div className="px-4 text-xs text-gray-500 break-all">
                  Transaction:{" "}
                  <a
                    href={`https://snowtrace.io/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {txHash.slice(0, 10)}...{txHash.slice(-8)}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Drawer */}
      {currentDrawer === "success" && nfcClaimResponse && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="space-y-2 text-center">
              <div className="text-lg font-bold text-center">🎉 CONGRATS!</div>

              <div className="text-[#626262]">
                {nfcClaimResponse?.action === "reclaim"
                  ? "Congratulations! Your Chimpers Digital Collectible has been reclaimed and is now in your wallet."
                  : "Congratulations! Your Chimpers Digital Collectible has been claimed."}
              </div>

              {txHash && (
                <div className="px-4 text-xs text-gray-500 break-all">
                  Transaction:{" "}
                  <a
                    href={`https://snowtrace.io/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {txHash.slice(0, 10)}...{txHash.slice(-8)}
                  </a>
                </div>
              )}

              <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mx-auto">
                <img
                  src={`/images/toys/${
                    nfcClaimResponse?.toyType !== undefined
                      ? nfcClaimResponse.toyType + 1
                      : 1
                  }.png`}
                  alt={`Toy Type ${
                    nfcClaimResponse?.toyType !== undefined
                      ? nfcClaimResponse.toyType
                      : 1
                  }`}
                  className="object-cover absolute inset-0 w-full h-full"
                  onError={(e) => {
                    // Fallback to dragon image if toy image not found
                    (e.target as HTMLImageElement).src = "/images/dragon.png";
                  }}
                />
              </div>

              <div className="text-center text-[22px] font-bold">
                Toy Type {nfcClaimResponse?.toyType || 1}
              </div>

              <div className="text-[#626262]">Certificate of Authenticity</div>

              <button
                onClick={() => navigate("/rewards/chimpers")}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-6"
              >
                Go to profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Already Claimed Drawer */}
      {currentDrawer === "already_claimed" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            <div className="text-center">
              <img
                src="/svg/wallet-connected.svg"
                alt="wallet-connected"
                className="mx-auto"
              />

              <h2 className="mb-2 text-xl font-bold">Already Claimed</h2>

              <p className="mb-6 text-gray-600">
                {error || "This Digital Collectible has already been claimed"}
              </p>

              <button
                onClick={() => navigate("/rewards/chimpers")}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800"
              >
                Go to profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Drawer */}
      {currentDrawer === "error" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img
              src="/svg/error.svg"
              alt="error"
              className="mb-4 w-32 h-32"
              onError={(e) => {
                // Fallback to inline SVG if image not found
                e.currentTarget.style.display = "none";
              }}
            />

            <div className="text-center space-y-2.5">
              <div className="text-2xl font-bold text-black">
                {error?.toLowerCase().includes("expired")
                  ? "Session Expired"
                  : error?.toLowerCase().includes("invalid")
                  ? "Invalid Session"
                  : error?.toLowerCase().includes("counter") ||
                    error?.toLowerCase().includes("many scans")
                  ? "Too Many Scans"
                  : "Connection Error"}
              </div>

              <div className="text-[#626262]">
                {error || "An unexpected error occurred. Please try again."}
              </div>

              <button
                onClick={() =>
                  window.open("/support", "_blank", "noopener,noreferrer")
                }
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-6"
              >
                Contact Support
              </button>

              <div className="flex items-center justify-center gap-1 text-sm text-gray-500 !mt-4">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>

                <span>
                  Please contact support if you believe this is an error.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CarouselCard = ({
  card,
}: {
  card: { id: number; image: string; title: string; description: string };
}) => {
  return (
    <div className="w-full rounded-[14px] overflow-hidden bg-gradient-to-br from-purple-400 via-purple-500 to-purple-600 shadow-xl mx-auto">
      <div className="relative w-full h-full rounded-[14px] overflow-hidden aspect-[3/4]">
        <img
          src={card.image}
          alt={card.title}
          className="object-cover absolute inset-0 w-full h-full"
        />
      </div>
    </div>
  );
};
